import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { confirmPrivacyOptOut } from "@/lib/privacy.functions";

export const Route = createFileRoute("/policies/privacy-confirm")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Confirm Your Privacy Choice — Coyote Creek Hat Co." },
      {
        name: "description",
        content:
          "Confirm your request to opt out of the sale or sharing of your personal information at Coyote Creek Hat Co.",
      },
      { property: "og:title", content: "Confirm Your Privacy Choice — Coyote Creek Hat Co." },
      {
        property: "og:description",
        content: "Confirm your privacy request for Coyote Creek Hat Co.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        property: "og:url",
        content: "https://coyotecreekhatco.com/policies/privacy-confirm",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PrivacyConfirmPage,
});

type State = "working" | "done" | "invalid";

function PrivacyConfirmPage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>("working");
  const [optedOut, setOptedOut] = useState(true);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setState("invalid");
      return;
    }

    confirmPrivacyOptOut({ data: { token } })
      .then((res) => {
        if (res.ok) {
          setOptedOut(res.optOut);
          setState("done");
        } else {
          setState("invalid");
        }
      })
      .catch(() => setState("invalid"));
  }, [token]);

  return (
    <div className="bg-background">
      <section className="bg-[#FDFAF3]">
        <div className="mx-auto max-w-[1400px] px-6 pt-8 pb-16 text-center">
          <h1 className="font-heading text-5xl uppercase tracking-wide text-primary md:text-6xl">
            Your Privacy Choices
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16">
        {state === "working" && (
          <p className="font-body text-base leading-relaxed text-foreground">
            Confirming your request…
          </p>
        )}

        {state === "done" && (
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid h-7 w-7 place-items-center rounded-full bg-green-600 text-white">
                <Check className="h-4 w-4" strokeWidth={3} />
              </span>
              <p className="font-heading text-xl uppercase tracking-wide text-primary">
                {optedOut
                  ? "Your opt-out is confirmed."
                  : "Data sharing has been resumed."}
              </p>
            </div>
            <p className="font-body text-base leading-relaxed text-foreground">
              {optedOut
                ? "We've applied your request to the account tied to this email address. You won't need to do anything else."
                : "We've resumed sharing for the account tied to this email address. You can opt out again at any time."}
            </p>
            <Link
              to="/policies/your-privacy-choices"
              className="inline-block font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#8B6F47] underline underline-offset-4 hover:text-blaze"
            >
              Back to Your Privacy Choices
            </Link>
          </div>
        )}

        {state === "invalid" && (
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid h-7 w-7 place-items-center rounded-full bg-[#2A1510] text-white">
                <X className="h-4 w-4" strokeWidth={3} />
              </span>
              <p className="font-heading text-xl uppercase tracking-wide text-primary">
                This link has expired or was already used.
              </p>
            </div>
            <p className="font-body text-base leading-relaxed text-foreground">
              Confirmation links are good for 24 hours and can only be used once. You can
              start a new request from the Your Privacy Choices page.
            </p>
            <Link
              to="/policies/your-privacy-choices"
              className="inline-block font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#8B6F47] underline underline-offset-4 hover:text-blaze"
            >
              Back to Your Privacy Choices
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
